import { makeObservable } from 'mobx';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { changeAccountContactPreferencesNew } from 'src_server/trpc/types/accountModel';
import { ContactPreferencesNewOptionType } from 'src_server/trpc/types/accountModel/changeAccountContactPreferencesNew';

type ContactPreferencesToggleListType = {
    [type in changeAccountContactPreferencesNew.ContactPreferencesNewOptionType]: boolean;
};
export interface ContactPreferencesFormatFormModelType {
    newContactPreferences: true;
    contactPreferences: changeAccountContactPreferencesNew.ContactPreferencesNewType[];
}

type ContactPreferencesGroupType = {
    [type in changeAccountContactPreferencesNew.ContactPreferencesNewKeyType]: changeAccountContactPreferencesNew.ContactPreferencesNewOptionType[];
};

export class ContactPreferencesToggleListOptionsState {
    public betting: FormInputState<boolean, boolean>;
    public casino: FormInputState<boolean, boolean>;
    public bingo: FormInputState<boolean, boolean>;

    public contactPreferencesGroup: FormModel<ContactPreferencesToggleListType>;
    public formatFormModel: FormModel<changeAccountContactPreferencesNew.ContactPreferencesNewOptionType[]>;

    public constructor(private readonly preferences?: ContactPreferencesNewOptionType[]) {
        this.betting = FormInputState.new(this.preferences?.includes('betting') ?? false);
        this.casino = FormInputState.new(this.preferences?.includes('casino') ?? false);
        this.bingo = FormInputState.new(this.preferences?.includes('bingo') ?? false);

        this.contactPreferencesGroup = FormModel.group({
            bingo: this.bingo,
            casino: this.casino,
            betting: this.betting,
        });

        this.formatFormModel = this.contactPreferencesGroup.map(
            (
                obj: ContactPreferencesToggleListType
            ): Result<changeAccountContactPreferencesNew.ContactPreferencesNewOptionType[]> => {
                return Result.createOk(
                    Object.entries(obj).reduce<changeAccountContactPreferencesNew.ContactPreferencesNewOptionType[]>(
                        (prev, [key, value]) => {
                            if (value) {
                                const parseKey =
                                    changeAccountContactPreferencesNew.ContactPreferencesOptionSchema.safeParse(key);
                                if (parseKey.success) {
                                    return [parseKey.data, ...prev];
                                }
                                return prev;
                            }
                            return prev;
                        },
                        []
                    )
                );
            }
        );

        makeObservable(this);
    }
}

export class ContactPreferencesToggleListState {
    public email: ContactPreferencesToggleListOptionsState;
    public phone: ContactPreferencesToggleListOptionsState;
    public sms: ContactPreferencesToggleListOptionsState;

    public contactPreferencesGroup: FormModel<ContactPreferencesGroupType>;
    public formatFormModel: FormModel<ContactPreferencesFormatFormModelType>;

    public constructor(
        private readonly emailPreferences?: ContactPreferencesNewOptionType[],
        private readonly phonePreferences?: ContactPreferencesNewOptionType[],
        private readonly smsPreferences?: ContactPreferencesNewOptionType[]
    ) {
        this.email = new ContactPreferencesToggleListOptionsState(this.emailPreferences);
        this.phone = new ContactPreferencesToggleListOptionsState(this.phonePreferences);
        this.sms = new ContactPreferencesToggleListOptionsState(this.smsPreferences);

        this.contactPreferencesGroup = FormModel.group({
            email: this.email.formatFormModel,
            sms: this.sms.formatFormModel,
            phone: this.phone.formatFormModel,
        });

        this.formatFormModel = this.contactPreferencesGroup.map(
            (obj): Result<ContactPreferencesFormatFormModelType> => {
                const result = Object.entries(obj)
                    .reduce<changeAccountContactPreferencesNew.ContactPreferencesNewType[]>((prev, [key, value]) => {
                        const parseResult =
                            changeAccountContactPreferencesNew.ContactPreferencesKeySchema.safeParse(key);
                        if (parseResult.success) {
                            const contactPreference: changeAccountContactPreferencesNew.ContactPreferencesNewType = {
                                type: parseResult.data,
                                categories: value,
                            };
                            return [contactPreference, ...prev];
                        }

                        return prev;
                    }, [])
                    .filter((item) => item.categories.length > 0);

                return Result.createOk({
                    newContactPreferences: true,
                    contactPreferences: result,
                });
            }
        );

        makeObservable(this);
    }
}
